import {
  Boxes,
  CircleHelp,
  Database,
  FileText,
  GitPullRequest,
  History,
  LayoutDashboard,
  LogOut,
  Menu,
  Monitor,
  MoonStar,
  Network,
  PanelLeftClose,
  PanelLeftOpen,
  RefreshCw,
  ScanSearch,
  ShieldCheck,
  SlidersHorizontal,
  Sun,
  UserRound,
  Wrench,
  X,
} from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { commandCenterApi, sessionAllows } from "../api/command-center.js";
import type {
  ConsoleSummary,
  PermissionAction,
  RouteId,
  SessionContext,
  ThemePreference,
} from "../api/types.js";
import { brand, consolePath } from "../app/brand.js";
import type { AppRoute } from "../app/routing.js";
import { handleInternalNavigation, isScanScopedPath, pathWithScanScope } from "../app/routing.js";
import type { ApiResource } from "../hooks/useApiResource.js";
import "./app-shell-nav.css";
import type { OperatorConnection, OperatorSourceClass } from "./OperatorContextStrip.js";
import { OperatorContextStrip } from "./OperatorContextStrip.js";
import { ThemeSwatches } from "./ThemeSwatches.js";

function tenantDisplayName(tenantId: string | undefined): string {
  if (!tenantId) return "loading";
  if (tenantId === "tenant_default") return `${brand.name} PoC`;
  return tenantId;
}

type NavSection = "Review" | "Govern" | "Settings";

interface NavItem {
  id: RouteId;
  label: string;
  path: string;
  icon: typeof ScanSearch;
  action: PermissionAction;
  section: NavSection;
  hidden?: boolean;
}

const NAV_SECTIONS: readonly NavSection[] = ["Review", "Govern", "Settings"];

const NAV_ITEMS: NavItem[] = [
  {
    id: "overview",
    label: "Overview",
    path: "/overview",
    icon: LayoutDashboard,
    action: "overview:read",
    section: "Review",
  },
  {
    id: "findings",
    label: "Findings",
    path: "/findings",
    icon: ScanSearch,
    action: "findings:read",
    section: "Review",
  },
  {
    id: "inventory",
    label: "SBOM + CBOM",
    path: "/inventory",
    icon: Boxes,
    action: "overview:read",
    section: "Review",
  },
  {
    id: "scans",
    label: "Scans",
    path: "/scans",
    icon: ScanSearch,
    action: "scans:read",
    section: "Review",
  },
  {
    id: "coverage",
    label: "Evidence",
    path: "/coverage",
    icon: ShieldCheck,
    action: "overview:read",
    section: "Review",
    hidden: true,
  },
  {
    id: "sources",
    label: "Sources",
    path: "/sources",
    icon: Database,
    action: "sources:read",
    section: "Review",
  },
  {
    id: "remediation",
    label: "Remediation",
    path: "/remediation",
    icon: Wrench,
    action: "remediation:read",
    section: "Govern",
  },
  {
    id: "threat-models",
    label: "Threat models",
    path: "/threat-models",
    icon: Network,
    action: "reports:read",
    section: "Govern",
  },
  {
    id: "delivery",
    label: "GitHub delivery",
    path: "/delivery",
    icon: GitPullRequest,
    action: "delivery:read",
    section: "Govern",
  },
  {
    id: "reports",
    label: "Reports",
    path: "/reports",
    icon: FileText,
    action: "reports:read",
    section: "Govern",
  },
  {
    id: "audit",
    label: "Audit trail",
    path: "/audit",
    icon: History,
    action: "audit:read",
    section: "Govern",
  },
  {
    id: "settings",
    label: "Model providers",
    path: "/settings",
    icon: SlidersHorizontal,
    action: "providers:read",
    section: "Settings",
  },
  {
    id: "admin",
    label: "Settings",
    path: "/admin",
    icon: ShieldCheck,
    action: "admin:read",
    section: "Settings",
  },
  {
    id: "help",
    label: "Support",
    path: "/help",
    icon: CircleHelp,
    action: "help:read",
    section: "Settings",
  },
];

function initialCollapsed(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const key = "provenance-nav-collapsed";
    const saved = window.localStorage.getItem(key);
    if (saved !== null) return saved === "true";
    const legacy = window.localStorage.getItem("assure-nav-collapsed");
    if (legacy !== null) {
      window.localStorage.setItem(key, legacy);
      return legacy === "true";
    }
    return false;
  } catch {
    return false;
  }
}

function operatorSourceClass(tenantId: string | undefined): OperatorSourceClass {
  const hay = (tenantId ?? "").toLowerCase();
  if (hay.includes("synthetic")) return "synthetic";
  if (hay.includes("benchmark")) return "public-benchmark";
  return "hosted-poc";
}

function operatorConnection(): OperatorConnection {
  if (typeof window === "undefined") return "hosted";
  const hostname = window.location.hostname;
  return hostname === "127.0.0.1" || hostname === "localhost" ? "loopback" : "hosted";
}

function ThemeControl({
  preference,
  setPreference,
}: {
  preference: ThemePreference;
  setPreference: (preference: ThemePreference) => void;
}) {
  const options: { value: ThemePreference; icon: typeof Sun }[] = [
    { value: "vantablack", icon: MoonStar },
    { value: "light", icon: Sun },
    { value: "system", icon: Monitor },
  ];
  const ActiveIcon = options.find((option) => option.value === preference)?.icon ?? Monitor;

  return (
    <details className="menu-popover theme-control">
      <summary className="icon-button" aria-label={`Theme: ${preference}`} title="Change theme">
        <ActiveIcon aria-hidden="true" size={17} />
      </summary>
      <div className="menu-popover__panel menu-popover__panel--right">
        <span className="menu-popover__title">Theme</span>
        <ThemeSwatches
          preference={preference}
          onChange={(next) => {
            setPreference(next);
          }}
        />
      </div>
    </details>
  );
}

export function AppShell({
  route,
  navigate,
  theme,
  setTheme,
  summary,
  selectedScanId,
  allScans = false,
  latestScanId,
  scanNamedOptions = [],
  session,
  children,
}: {
  route: AppRoute;
  navigate: (path: string) => void;
  theme: ThemePreference;
  setTheme: (preference: ThemePreference) => void;
  summary: ApiResource<ConsoleSummary>;
  selectedScanId?: string | undefined;
  allScans?: boolean | undefined;
  latestScanId?: string | undefined;
  scanNamedOptions?: ReadonlyArray<{ value: string; label: string }> | undefined;
  session: ApiResource<SessionContext>;
  children: ReactNode;
}) {
  const [collapsed, setCollapsed] = useState(initialCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountMessage, setAccountMessage] = useState("");
  const mobileMenuRef = useRef<HTMLButtonElement>(null);
  const scanScoped = isScanScopedPath(route.path);
  const scanValue = allScans ? "__all__" : (selectedScanId ?? latestScanId ?? "");

  useEffect(() => {
    if (route.path) setMobileOpen(false);
  }, [route.path]);

  useEffect(() => {
    if (!mobileOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setMobileOpen(false);
      window.requestAnimationFrame(() => mobileMenuRef.current?.focus());
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [mobileOpen]);

  useEffect(() => {
    const closeMenusOnOutsidePointer = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      document
        .querySelectorAll<HTMLDetailsElement>("details.menu-popover[open]")
        .forEach((menu) => {
          if (!menu.contains(target)) menu.removeAttribute("open");
        });
    };

    document.addEventListener("pointerdown", closeMenusOnOutsidePointer);
    return () => document.removeEventListener("pointerdown", closeMenusOnOutsidePointer);
  }, []);

  const closeMobileNavigation = () => {
    setMobileOpen(false);
    window.requestAnimationFrame(() => mobileMenuRef.current?.focus());
  };

  const toggleCollapsed = () => {
    setCollapsed((current) => {
      const next = !current;
      try {
        window.localStorage.setItem("provenance-nav-collapsed", String(next));
      } catch {
        // Navigation remains usable when persistence is unavailable.
      }
      return next;
    });
  };

  const applyScanScope = (scanId: string | undefined, nextAllScans: boolean) => {
    if (scanScoped) {
      navigate(pathWithScanScope(`${route.path}${route.search}`, scanId, nextAllScans));
      return;
    }
    const url = new URL(`${route.path}${route.search}`, "https://q2.invalid");
    if (nextAllScans) {
      url.searchParams.delete("scanId");
      url.searchParams.set("scanScope", "all");
    } else if (scanId) {
      url.searchParams.set("scanId", scanId);
      url.searchParams.delete("scanScope");
    }
    navigate(`${url.pathname}${url.search}${url.hash}`);
  };

  return (
    <div className={`app-shell${collapsed ? " app-shell--collapsed" : ""}`}>
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      {mobileOpen ? (
        <button
          className="nav-backdrop"
          type="button"
          aria-label="Close navigation"
          aria-controls="primary-navigation"
          onClick={closeMobileNavigation}
        />
      ) : null}
      <aside
        className={`sidebar${mobileOpen ? " sidebar--open" : ""}`}
        id="primary-navigation"
        aria-label="Primary"
      >
        <div className="sidebar__brand">
          <img
            className="brand-lockup brand-lockup--dark"
            src={consolePath("/brand/breakwater-provenance-horizontal-dark-no-tagline.svg")}
            width="180"
            height="32"
            alt="Breakwater Provenance"
          />
          <img
            className="brand-lockup brand-lockup--light"
            src={consolePath("/brand/breakwater-provenance-horizontal-light-no-tagline.svg")}
            width="180"
            height="32"
            alt=""
            aria-hidden="true"
          />
          <img
            className="brand-mark"
            src={consolePath("/brand/provenance-mark-dark.svg")}
            width="36"
            height="36"
            alt=""
            aria-hidden="true"
          />
          <button
            className="icon-button sidebar__mobile-close"
            type="button"
            aria-label="Close navigation"
            aria-controls="primary-navigation"
            title="Close navigation"
            onClick={closeMobileNavigation}
          >
            <X aria-hidden="true" size={18} />
          </button>
        </div>
        <nav className="sidebar__nav">
          {NAV_SECTIONS.map((section) => {
            const items = NAV_ITEMS.filter(
              (item) =>
                item.section === section &&
                !item.hidden &&
                sessionAllows(session.data, item.action),
            );
            if (items.length === 0) return null;
            return (
              <div className="nav-section" key={section}>
                <span className="nav-section-label">{section}</span>
                {items.map((item) => {
                  const Icon = item.icon;
                  const active = route.id === item.id;
                  const href = consolePath(pathWithScanScope(item.path, selectedScanId, allScans));
                  return (
                    <a
                      className="nav-link"
                      data-active={active ? "true" : undefined}
                      href={href}
                      aria-current={active ? "page" : undefined}
                      title={collapsed ? item.label : undefined}
                      key={item.id}
                      onClick={(event) => handleInternalNavigation(event, navigate)}
                    >
                      <Icon aria-hidden="true" size={18} />
                      <span>{item.label}</span>
                    </a>
                  );
                })}
              </div>
            );
          })}
          <a className="nav-link" href="/app/docs" title={collapsed ? "Documentation" : undefined}>
            <FileText aria-hidden="true" size={18} />
            <span>Documentation</span>
          </a>
        </nav>
        <div className="sidebar__footer">
          <span className="environment-indicator">
            <span aria-hidden="true" />
            Hosted PoC · static only
          </span>
          <button
            className="nav-link nav-link--button"
            type="button"
            aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
            aria-controls="primary-navigation"
            aria-expanded={!collapsed}
            onClick={toggleCollapsed}
            title={collapsed ? "Expand navigation" : "Collapse navigation"}
          >
            {collapsed ? (
              <PanelLeftOpen aria-hidden="true" size={18} />
            ) : (
              <PanelLeftClose aria-hidden="true" size={18} />
            )}
            <span>{collapsed ? "Expand" : "Collapse"}</span>
          </button>
        </div>
      </aside>

      <div className="workspace">
        <header className="topbar">
          <button
            ref={mobileMenuRef}
            className="icon-button topbar__mobile-menu"
            type="button"
            aria-label="Open navigation"
            aria-controls="primary-navigation"
            aria-expanded={mobileOpen}
            title="Open navigation"
            onClick={() => setMobileOpen(true)}
          >
            <Menu aria-hidden="true" size={19} />
          </button>
          <div className="topbar__title">
            <span>Security review</span>
            <strong>{route.title}</strong>
          </div>
          <label className="topbar__scan-selector">
            <span>Scan</span>
            <select
              aria-label="Select scan"
              value={scanValue}
              disabled={scanNamedOptions.length === 0}
              onChange={(event) => {
                const value = event.target.value;
                applyScanScope(value === "__all__" ? undefined : value, value === "__all__");
              }}
            >
              <option value="__all__">All scans</option>
              {scanNamedOptions.map((option) => (
                <option value={option.value} key={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <div className="topbar__actions">
            <button
              className="icon-button"
              type="button"
              aria-label="Refresh console status"
              title="Refresh console status"
              disabled={summary.isRefreshing}
              onClick={summary.refresh}
            >
              <RefreshCw
                aria-hidden="true"
                className={summary.isRefreshing ? "spin" : undefined}
                size={17}
              />
            </button>
            <ThemeControl preference={theme} setPreference={setTheme} />
            <details className="menu-popover account-menu">
              <summary className="operator-avatar" aria-label="Open account menu" title="Account">
                <UserRound aria-hidden="true" size={16} />
              </summary>
              <div className="menu-popover__panel menu-popover__panel--right account-menu__panel">
                <span className="menu-popover__title">Session</span>
                {session.data ? (
                  <dl className="account-details">
                    <div>
                      <dt>Principal</dt>
                      <dd>{session.data.principalId}</dd>
                    </div>
                    <div>
                      <dt>Tenant</dt>
                      <dd>{tenantDisplayName(session.data.tenantId)}</dd>
                    </div>
                    <div>
                      <dt>Role</dt>
                      <dd>{session.data.role}</dd>
                    </div>
                    {session.data.scopeRevision ? (
                      <div>
                        <dt>Scope</dt>
                        <dd>{session.data.scopeRevision}</dd>
                      </div>
                    ) : null}
                  </dl>
                ) : (
                  <span className="muted-copy">
                    {session.error ? "Session unavailable" : "Loading session…"}
                  </span>
                )}
                <button
                  className="menu-item"
                  type="button"
                  disabled={!session.data}
                  onClick={() => {
                    setAccountMessage("Signing out…");
                    void commandCenterApi
                      .signOut()
                      .then(() => window.location.assign(consolePath("/login")))
                      .catch(() => setAccountMessage("Sign out failed. Try again."));
                  }}
                >
                  <LogOut aria-hidden="true" size={15} />
                  <span>Sign out</span>
                </button>
                <span className="account-menu__message" aria-live="polite">
                  {accountMessage}
                </span>
              </div>
            </details>
          </div>
        </header>
        <main id="main-content" className="main-content" tabIndex={-1}>
          {children}
        </main>
      </div>
    </div>
  );
}
